/**
 * Phase 24.4b Step 6 — data-fetching layer for the meal-plan view.
 *
 * Owns useQuery for current plan + useMutation for regenerate. Renders
 * WeekHeader + WeekGrid. Opens RecipeDetailModal + SwapDialog via
 * useMealPlanUi store.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarRange, ShoppingCart } from "lucide-react";
import {
  generateMealPlan,
  getCurrentMealPlan,
  type MealPlanRead,
} from "@/api/meal_plan";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/stores/toast";
import { useMealPlanUi } from "@/stores/meal_plan_ui";
import { WeekHeader } from "./WeekHeader";
import { WeekGrid } from "./WeekGrid";
import { RecipeDetailModal } from "./RecipeDetailModal";
import { SwapDialog } from "./SwapDialog";

function thisMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export function MealPlanContainer() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { show } = useToast();
  const { modalRecipe, setModalRecipe, swapDialogOpen, setSwapDialogOpen } =
    useMealPlanUi();

  const planQ = useQuery<MealPlanRead | null>({
    queryKey: ["meal-plan-current"],
    queryFn: async () => {
      try {
        return await getCurrentMealPlan();
      } catch (err) {
        // 404 = no plan yet; surface as empty state, not error
        const status =
          err && typeof err === "object" && "response" in err
            ? // axios error
              ((err as { response?: { status?: number } }).response?.status ??
                500)
            : 500;
        if (status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });

  const regenMut = useMutation({
    mutationFn: () => generateMealPlan(thisMonday()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meal-plan-current"] });
      qc.invalidateQueries({ queryKey: ["shopping-list"] });
      show({ message: t("meal_plan.regenerate_success"), variant: "success" });
    },
    onError: (error) => {
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } } | null)?.response;
      const sourceRestricted = response?.status === 409 && response.data?.detail === "strava_source_restricted";
      show({ message: t(sourceRestricted ? "workout_detail.metrics_unavailable_note" : "meal_plan.regenerate_failed"), variant: "error" });
    },
  });

  if (planQ.isLoading) {
    return (
      <div className="space-y-3" data-testid="meal-plan-loading">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (planQ.isError) {
    return (
      <EmptyState
        variant="hero"
        icon={CalendarRange}
        title={t("meal_plan.load_failed_title")}
        description={t("meal_plan.load_failed_desc")}
      >
        <Button onClick={() => planQ.refetch()}>{t("meal_plan.retry")}</Button>
      </EmptyState>
    );
  }

  if (!planQ.data) {
    return (
      <EmptyState
        variant="hero"
        icon={CalendarRange}
        title={t("meal_plan.no_plan_title")}
        description={t("meal_plan.no_plan_desc")}
      >
        <Button
          onClick={() => regenMut.mutate()}
          disabled={regenMut.isPending}
          data-testid="generate-first-plan"
        >
          {regenMut.isPending
            ? t("meal_plan.regenerating")
            : t("meal_plan.generate_first")}
        </Button>
      </EmptyState>
    );
  }

  const plan = planQ.data;
  return (
    <div className="space-y-4">
      <WeekHeader
        weekStartDate={plan.week_start_date}
        warnings={plan.warnings}
        onRegenerate={() => regenMut.mutate()}
        isRegenerating={regenMut.isPending}
      />
      {/* Phase 24.7.4 T3 — link to shopping list page */}
      <Link
        to={`/shopping-list/${plan.id}`}
        className="inline-flex items-center gap-1.5 self-start rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[12px] font-medium text-amber-dark transition-colors hover:border-amber/50 hover:bg-cream-border/40"
        data-testid="open-shopping-list"
      >
        <ShoppingCart className="h-3.5 w-3.5" strokeWidth={2} />
        {t("meal_plan.open_shopping_list")}
      </Link>
      <WeekGrid
        plan={plan}
        onMealClick={(itemId, recipeId) =>
          setModalRecipe({ itemId, recipeId })
        }
      />
      {modalRecipe ? (
        <RecipeDetailModal
          planId={plan.id}
          itemId={modalRecipe.itemId}
          recipeId={modalRecipe.recipeId}
          onClose={() => setModalRecipe(null)}
        />
      ) : null}
      {modalRecipe && swapDialogOpen ? (
        <SwapDialog
          isOpen={swapDialogOpen}
          onClose={() => setSwapDialogOpen(false)}
          planId={plan.id}
          itemId={modalRecipe.itemId}
        />
      ) : null}
    </div>
  );
}
